import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { LoggerService } from '@netfoundry-ui/shared/services';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface EdgeRouterConnection {
    url: string;
    status: ConnectionStatus;
}

export interface ZitiConnectionState {
    controller: ConnectionStatus;
    controllerUrl: string;
    edgeRouters: EdgeRouterConnection[];
}

/**
 * One status for the whole overlay: connected only when the controller and at least one edge router are.
 */
export function overallConnectionStatus(state: ZitiConnectionState): ConnectionStatus {
    if (state.controller === 'connected' && state.edgeRouters.some((r) => r.status === 'connected')) {
        return 'connected';
    }
    if (state.controller === 'error' || state.edgeRouters.some((r) => r.status === 'error')) {
        return 'error';
    }
    if (state.controller === 'connecting' || state.edgeRouters.some((r) => r.status === 'connecting')) {
        return 'connecting';
    }
    return 'disconnected';
}

export interface ZitiMetrics {
    requestCount: number;
    requestErrors: number;
    totalBytesSent: number;
    totalBytesReceived: number;
    avgRequestDurationMs: number;
    lastRequestDurationMs: number;
    reconnects: number;
}

// Shipped gzipped: about 9 MB instead of 59 MB, and CDNs do not compress files that large on the fly
const WASM_BINARY_PATH = 'assets/wasm/main.wasm.gz';

/**
 * Decompress a gzip payload in the browser. Anything else passes through, in case a CDN already decoded it.
 */
async function gunzipIfNeeded(bytes: Uint8Array): Promise<Uint8Array> {
    if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) {
        return bytes;
    }
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

// Globals registered by the Go WASM runtime (wasm_exec.js + main.go)
declare const Go: any;
declare const zitiConnectWithToken: (url: string, jwt: string) => Promise<string>;
declare const zitiConnectWithSession: (url: string, token: string, tokenType: string) => Promise<string>;
declare const zitiDisconnect: () => Promise<void>;
declare const zitiDialService: (name: string, msg: string, onData: (chunk: string) => void) => Promise<string>;
declare const zitiSSHConnect: (serviceName: string, cols: number, rows: number, username: string, password: string, onData: (data: string) => void) => Promise<string>;
declare const zitiSSHSend: (data: string) => void;
declare const zitiSSHResize: (cols: number, rows: number) => void;
declare const zitiSSHDisconnect: () => Promise<void>;
declare const zitiGetEdgeRouterUrls: () => string;

@Injectable({
    providedIn: 'root',
})
export class ZitiWasmService {
    /** Emits true once the WASM context is connected and services are available for dialing. */
    ready$ = new BehaviorSubject<boolean>(false);
    /** Controls visibility of the metrics panel. */
    metricsPanelOpen$ = new BehaviorSubject<boolean>(false);
    /** Emits when the user asks for a fresh secure connection after a failed or cancelled one. */
    retryRequested$ = new Subject<void>();
    connectionState$ = new BehaviorSubject<ZitiConnectionState>({
        controller: 'disconnected',
        controllerUrl: '',
        edgeRouters: [],
    });
    metrics$ = new BehaviorSubject<ZitiMetrics>({
        requestCount: 0,
        requestErrors: 0,
        totalBytesSent: 0,
        totalBytesReceived: 0,
        avgRequestDurationMs: 0,
        lastRequestDurationMs: 0,
        reconnects: 0,
    });

    currentControllerUrl = '';
    currentIdToken = '';
    serviceAvailable = false;

    private wasmLoaded = false;
    private loadingPromise: Promise<void> | null = null;
    private requestDurations: number[] = [];

    constructor(private logger: LoggerService) {}

    updateConnectionState(partial: Partial<ZitiConnectionState>) {
        const current = this.connectionState$.getValue();
        this.connectionState$.next({ ...current, ...partial });
    }

    setEdgeRouters(routers: EdgeRouterConnection[]) {
        const current = this.connectionState$.getValue();
        this.connectionState$.next({ ...current, edgeRouters: routers });
    }

    refreshEdgeRouterState() {
        if (!this.wasmLoaded) return;
        try {
            const json = zitiGetEdgeRouterUrls();
            const urls: string[] = JSON.parse(json);
            const routers: EdgeRouterConnection[] = urls.map((url) => ({
                url,
                status: 'connected' as ConnectionStatus,
            }));
            this.setEdgeRouters(routers);
        } catch {
            // ignore
        }
    }

    recordRequest(bytesSent: number, bytesReceived: number, durationMs: number, error: boolean) {
        const m = this.metrics$.getValue();
        this.requestDurations.push(durationMs);
        const avg = this.requestDurations.reduce((a, b) => a + b, 0) / this.requestDurations.length;
        this.metrics$.next({
            requestCount: m.requestCount + 1,
            requestErrors: m.requestErrors + (error ? 1 : 0),
            totalBytesSent: m.totalBytesSent + bytesSent,
            totalBytesReceived: m.totalBytesReceived + bytesReceived,
            avgRequestDurationMs: Math.round(avg),
            lastRequestDurationMs: Math.round(durationMs),
            reconnects: m.reconnects,
        });
    }

    recordReconnect() {
        const m = this.metrics$.getValue();
        this.metrics$.next({ ...m, reconnects: m.reconnects + 1 });
    }

    resetMetrics() {
        this.requestDurations = [];
        this.metrics$.next({
            requestCount: 0, requestErrors: 0, totalBytesSent: 0,
            totalBytesReceived: 0, avgRequestDurationMs: 0,
            lastRequestDurationMs: 0, reconnects: 0,
        });
        this.connectionState$.next({ controller: 'disconnected', controllerUrl: '', edgeRouters: [] });
    }

    /**
     * One-time load of wasm_exec.js + main.wasm.
     * Safe to call multiple times — subsequent calls are no-ops.
     */
    async loadWasm(): Promise<void> {
        if (this.wasmLoaded) return;
        if (this.loadingPromise) return this.loadingPromise;

        this.loadingPromise = this._doLoadWasm();
        return this.loadingPromise;
    }

    private async _doLoadWasm(): Promise<void> {
        try {
            this.logger.info('ZitiWasmService: loading WASM runtime...');

            const go = new Go();

            // Fetch with progress tracking so callers can show download state
            const response = await fetch(WASM_BINARY_PATH);
            if (!response.ok) {
                throw new Error(`failed to fetch ${WASM_BINARY_PATH}: HTTP ${response.status}`);
            }
            // With a content-encoding the header holds the encoded size, not the bytes read below
            const contentLength = response.headers.get('content-encoding') ? 0 : Number(response.headers.get('content-length') || '0');
            const reader = response.body?.getReader();

            if (!reader) {
                throw new Error('ReadableStream not supported');
            }

            const chunks: Uint8Array[] = [];
            let received = 0;

            for (let next = await reader.read(); !next.done; next = await reader.read()) {
                const value = next.value;
                chunks.push(value);
                received += value.length;
                if (this._onLoadProgress) {
                    if (contentLength > 0) {
                        this._onLoadProgress({ percent: Math.round((received / contentLength) * 100), receivedMB: received / (1024 * 1024) });
                    } else {
                        this._onLoadProgress({ percent: -1, receivedMB: received / (1024 * 1024) });
                    }
                }
            }

            // Combine chunks into a single buffer
            const downloaded = new Uint8Array(received);
            let offset = 0;
            for (const chunk of chunks) {
                downloaded.set(chunk, offset);
                offset += chunk.length;
            }

            const wasmBytes = await gunzipIfNeeded(downloaded);
            const result = await WebAssembly.instantiate(wasmBytes as BufferSource, go.importObject);

            // Start the Go runtime (non-blocking — it runs in a goroutine loop)
            go.run(result.instance);

            this.wasmLoaded = true;
            this._onLoadProgress = null;
            this.logger.info('ZitiWasmService: WASM runtime loaded successfully');
        } catch (err) {
            this.loadingPromise = null;
            this._onLoadProgress = null;
            this.logger.error(`ZitiWasmService: failed to load WASM runtime: ${err}`);
            throw err;
        }
    }

    /** Register a callback to receive WASM download progress. percent is 0–100 when Content-Length is known, or -1 when unknown. */
    set onLoadProgress(fn: ((progress: { percent: number; receivedMB: number }) => void) | null) {
        this._onLoadProgress = fn;
    }
    private _onLoadProgress: ((progress: { percent: number; receivedMB: number }) => void) | null = null;

    /**
     * Connect to a Ziti controller using the Auth0 ID token (JWT).
     * The Go SDK authenticates directly with the controller via ext-jwt.
     */
    async connect(controllerUrl: string, idToken: string): Promise<string> {
        await this.loadWasm();

        // Disconnect existing context if controller or token changed
        if (this.currentControllerUrl && (
            this.currentControllerUrl !== controllerUrl ||
            this.currentIdToken !== idToken
        )) {
            await this.disconnect();
        }

        this.logger.info(`ZitiWasmService: connecting to ${controllerUrl}`);

        const result = await zitiConnectWithToken(controllerUrl, idToken);

        this.currentControllerUrl = controllerUrl;
        this.currentIdToken = idToken;

        this.logger.info(`ZitiWasmService: connected successfully`);
        return result;
    }

    /**
     * Connect to a Ziti controller using a pre-existing session token
     * obtained via the NF exchange endpoint. Uses AuthenticateWithPreviousSession
     * to refresh the session without a full OIDC flow.
     * tokenType should be "SESSION" (zt-session) or "BEARER" (OIDC access token).
     */
    async connectWithSession(controllerUrl: string, token: string, tokenType: string): Promise<string> {
        await this.loadWasm();

        // Disconnect existing context if controller or token changed
        if (this.currentControllerUrl && (
            this.currentControllerUrl !== controllerUrl ||
            this.currentIdToken !== token
        )) {
            await this.disconnect();
        }

        this.logger.info(`ZitiWasmService: connecting to ${controllerUrl} with previous session (${tokenType})`);

        const result = await zitiConnectWithSession(controllerUrl, token, tokenType);

        this.currentControllerUrl = controllerUrl;
        this.currentIdToken = token;

        this.logger.info(`ZitiWasmService: connected successfully via previous session`);
        return result;
    }

    /**
     * Disconnect the current Ziti context and clear state.
     */
    async disconnect(): Promise<void> {
        if (!this.wasmLoaded) return;

        try {
            await zitiDisconnect();
        } catch (err) {
            this.logger.warn(`ZitiWasmService: disconnect error (non-fatal): ${err}`);
        }

        this.currentControllerUrl = '';
        this.currentIdToken = '';
        this.serviceAvailable = false;
    }

    /**
     * Dial a Ziti service and stream the response via callback.
     */
    async dialService(serviceName: string, message: string, onData: (chunk: string) => void): Promise<string> {
        if (!this.wasmLoaded) {
            throw new Error('ZitiWasmService: WASM not loaded');
        }
        return zitiDialService(serviceName, message, onData);
    }


    /**
     * Connect to an SSH service via Ziti and open an interactive terminal.
     * The onData callback streams terminal output back to the caller.
     * Username and password are used for the ziti edge login command
     * that runs before the interactive shell starts.
     */
    async sshConnect(
        serviceName: string,
        cols: number,
        rows: number,
        username: string,
        password: string,
        onData: (data: string) => void
    ): Promise<string> {
        if (!this.wasmLoaded) {
            throw new Error('ZitiWasmService: WASM not loaded');
        }
        return zitiSSHConnect(serviceName, cols, rows, username, password, onData);
    }

    /**
     * Send keyboard input to the active SSH session.
     */
    sshSend(data: string): void {
        if (!this.wasmLoaded) return;
        zitiSSHSend(data);
    }

    /**
     * Notify the SSH session of a terminal resize.
     */
    sshResize(cols: number, rows: number): void {
        if (!this.wasmLoaded) return;
        zitiSSHResize(cols, rows);
    }

    /**
     * Disconnect the active SSH session.
     */
    async sshDisconnect(): Promise<void> {
        if (!this.wasmLoaded) return;
        return zitiSSHDisconnect();
    }
}
